import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { getPage } from '../pages'
import JSONPretty from 'react-json-pretty'
import { Collapse, IconButton } from '@material-ui/core'
import { ExpandLess, ExpandMore } from '@material-ui/icons'

const Content = () => {

  const location = useLocation()
  const history = useHistory()

  const jsonTheme = {
    main: 'line-height:1.3;color:#848788;overflow:auto;font-size:11px',
    key: 'color:#000b5f;',
    string: 'color:#007bff;',
    value: 'color:#aa20f0;',
    boolean: 'color:#ac81fe;',
  }

  const baseUrl = 'https://assuranceski-api.web.app'
  // const baseUrl = 'http://localhost:8080'

  const [currentPage, setCurrentPage] = useState('intro')
  const [activeAnchor, setActiveAnchor] = useState('')
  const activeAnchorRef = useRef('')

  const activeAnchorSettingTimeoutRef = useRef(null)

  const [sectionsCollapsed, setSectionsCollapsed] = useState({})

  const requestItemLayoutStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 1fr) minmax(90px, 0.8fr) minmax(100px, 0.9fr) minmax(220px, 1.6fr)',
    columnGap: '12px',
    rowGap: '6px',
    alignItems: 'center',
  }

  const requestItemCellStyle = {
    width: 'auto',
    minWidth: 0,
  }

  const requestItemDescriptionStyle = {
    ...requestItemCellStyle,
    wordBreak: 'break-word',
  }

  const requestItemRowStyle = {
    ...requestItemLayoutStyle,
    padding: '8px 0',
    borderBottom: '1px solid #dfe3e6',
  }

  const requestItemColumnStyles = [
    { ...requestItemCellStyle, paddingRight: '12px', borderRight: '1px solid #e6e9ed' },
    { ...requestItemCellStyle, padding: '0 12px', borderRight: '1px solid #e6e9ed' },
    { ...requestItemCellStyle, padding: '0 12px', borderRight: '1px solid #e6e9ed' },
    { ...requestItemDescriptionStyle, paddingLeft: '12px' },
  ]

  const getRequestRowStyle = (isLastRow) => ({
    ...requestItemRowStyle,
    borderBottom: isLastRow ? 'none' : requestItemRowStyle.borderBottom,
  })

  const getAnchorId = (section) => {
    const path = section?.anchor
    return path || ''
    // if (!path || typeof path !== 'string') return ''
    // const hashIndex = path.indexOf('#')
    // if (hashIndex === -1) return ''
    // return path.substring(hashIndex + 1)
  }

  const getApiPath = (section) => section?.path || ''

  const requestId = (s) => {
    return `${s.method}_${getApiPath(s)}`
  }

  const pageContent = useMemo(() => getPage(currentPage), [currentPage])
  const anchorIds = useMemo(() => pageContent.map((s) => getAnchorId(s)).filter(Boolean), [pageContent])

  useEffect(() => {
    activeAnchorRef.current = activeAnchor
  }, [activeAnchor])

  const toggleSection = (id) => {
    setSectionsCollapsed({
      ...sectionsCollapsed,
      [id]: !Boolean(sectionsCollapsed[id])
    })
  }

  const extractRefName = (ref) => {
    if (!ref || typeof ref !== 'string') return ''
    const chunks = ref.split('/')
    return chunks[chunks.length - 1]
  }

  const normalizeSchema = (schema, components) => {
    if (!schema) return null
    const componentSchemas = components?.schemas || {}
    if (schema.$ref) {
      const refName = extractRefName(schema.$ref)
      const resolved = componentSchemas[refName] || {}
      return {
        refName,
        ...resolved,
        ...schema,
        type: schema.type || resolved.type,
        description: schema.description || resolved.description,
        example: schema.example !== undefined ? schema.example : resolved.example,
        properties: schema.properties || resolved.properties,
        items: schema.items || resolved.items,
        required: schema.required || resolved.required,
        oneOf: schema.oneOf || resolved.oneOf,
      }
    }
    return {
      ...schema,
      type: schema.type,
      description: schema.description,
      example: schema.example,
      properties: schema.properties,
      items: schema.items,
      required: schema.required,
      oneOf: schema.oneOf,
    }
  }

  const getSchemaTypeLabel = (schema, components) => {
    if (!schema) return ''
    if (schema.type === 'array') {
      return `array<${getSchemaTypeLabel(schema.items, components)}>`
    }
    if (schema.oneOf && Array.isArray(schema.oneOf)) {
      return schema.oneOf.map((s) => getSchemaTypeLabel(s, components)).join(' | ')
    }
    if (schema.$ref) {
      return extractRefName(schema.$ref)
    }
    if (schema.refName) {
      return schema.refName
    }
    return schema.type || 'object'
  }

  const collectSchemaProperties = (schema, components, parentKey = '', required = false) => {
    const normalized = normalizeSchema(schema, components)
    if (!normalized) return []
    const properties = []
    const typeLabel = getSchemaTypeLabel(schema, components)
    const schemaType = normalized.type || typeLabel

    if (parentKey) {
      properties.push({
        key: parentKey,
        required,
        type: typeLabel,
        description: normalized.description,
        example: normalized.example,
      })
    }

    if ((schemaType === 'object' || normalized.properties) && normalized.properties) {
      const requiredKeys = normalized.required || []
      Object.entries(normalized.properties).forEach(([childKey, childSchema]) => {
        const childPath = parentKey ? `${parentKey}.${childKey}` : childKey
        properties.push(...collectSchemaProperties(childSchema, components, childPath, requiredKeys.includes(childKey)))
      })
    } else if (schemaType === 'array' || normalized.items) {
      const itemPath = parentKey ? `${parentKey}[]` : 'items'
      properties.push(...collectSchemaProperties(normalized.items, components, itemPath, true))
    }

    return properties
  }

  const buildExampleFromSchema = (schema, components) => {
    const normalized = normalizeSchema(schema, components)
    if (!normalized) return undefined

    if (normalized.example !== undefined) {
      return normalized.example
    }

    if (normalized.oneOf && normalized.oneOf.length > 0) {
      return buildExampleFromSchema(normalized.oneOf[0], components)
    }

    const schemaType = normalized.type || getSchemaTypeLabel(schema, components)

    if ((schemaType === 'object' || normalized.properties) && normalized.properties) {
      const obj = {}
      Object.entries(normalized.properties).forEach(([key, childSchema]) => {
        const value = buildExampleFromSchema(childSchema, components)
        if (value !== undefined) {
          obj[key] = value
        }
      })
      return obj
    }

    if (schemaType === 'array' || normalized.items) {
      const value = buildExampleFromSchema(normalized.items, components)
      return value !== undefined ? [value] : []
    }

    return normalized.example
  }

  const formatInlineExample = (example, typeLabel) => {
    if (example === null) return 'null'
    if (typeof example === 'object') return JSON.stringify(example)
    if ((typeLabel || '').includes('string')) {
      return `"${example}"`
    }
    return `${example}`
  }

  const renderPropertyList = (properties, keyPrefix) => {
    if (!properties || properties.length === 0) return null
    return (
      <div className="endpoint__request-section-wrapper">
        { properties.map((p, idx) => (
          <div key={`${keyPrefix}_${idx}`} className="endpoint__request-item" style={getRequestRowStyle(idx === properties.length - 1)}>
            <div className="endpoint__request-item-key" style={requestItemColumnStyles[0]}>{p.key}</div>
            <div className={`endpoint__request-item-level ${p.required ? 'required' : 'optional'}`} style={requestItemColumnStyles[1]}>{p.required ? 'required' : 'optional'}</div>
            <div className="endpoint__request-item-type" style={requestItemColumnStyles[2]}>{p.type}</div>
            <div className="endpoint__request-item-description" style={requestItemColumnStyles[3]}>
              { p.description && <div dangerouslySetInnerHTML={{ __html: p.description }} /> }
              { p.example !== undefined && (
                <div style={{ fontSize: '12px', color: '#555' }}>
                  Example: <code>{formatInlineExample(p.example, p.type)}</code>
                </div>
              ) }
            </div>
          </div>
        )) }
      </div>
    )
  }
  

  useEffect(() => {
    setCurrentPage(location.pathname.substring(1))
    const hash = location.hash ? location.hash.substring(1) : ''
    setActiveAnchor(hash)
    const parent = document.getElementsByClassName('column content')
    const scrollToAnchor = () => {
      const container = parent && parent[0]
      if (!container) return
      if (hash) {
        const el = document.getElementById(hash)
        if (el) {
          const offsetTop = el.offsetTop - 80
          container.scrollTo({ top: offsetTop < 0 ? 0 : offsetTop, behavior: 'smooth' })
        }
      } else {
        container.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    requestAnimationFrame(scrollToAnchor)
    activeAnchorSettingTimeoutRef.current = setTimeout(() => {
      activeAnchorSettingTimeoutRef.current = null
    }, 300)
  }, [location.hash, location.pathname])

  useEffect(() => {
    const parent = document.getElementsByClassName('column content')
    const container = parent && parent[0]
    if (!container || anchorIds.length === 0) return

    const headerOffset = 80
    const handleScroll = () => {
      let newActive = anchorIds[0] || ''
      for (const id of anchorIds) {
        const el = document.getElementById(id)
        if (!el) continue
        const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top
        if (top - headerOffset <= 0) {
          newActive = id
        } else {
          break
        }
      }
      if (newActive && newActive !== activeAnchorRef.current && !activeAnchorSettingTimeoutRef.current) {
        activeAnchorRef.current = newActive
        setActiveAnchor(newActive)
        const newHash = `#${newActive}`
        const nextUrl = `${location.pathname}${location.search || ''}${newHash}`
        if (window.location.hash !== newHash) {
          history.replace(nextUrl)
        }
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => container.removeEventListener('scroll', handleScroll)
  }, [anchorIds, history, location.pathname, location.search])

  const renderSection = (s, si) => {
    const anchorId = getAnchorId(s)
    const textTypes = ['title', 'subtitle', 'text', 'list_item', 'semi_title']
    if(textTypes.includes(s.type)) {
      return (
        <div id={anchorId || undefined} key={`content_section_${si}`} className={`content ${s.type}`} dangerouslySetInnerHTML={{ __html: s.content }}>
        </div>
      )
    } else if(s.type === 'separator') {
      return <div id={anchorId || undefined} key={`content_section_${si}`} className="content separator" />
    } else if(s.type === 'endpoint') {
      return renderEndpoint(s, si)
    } else if(s.type === 'webhook') {
      return renderWebhook(s, si)
    }
  }

  const curlExample = (s) => {
    const request = s.request || {}
    const apiPath = getApiPath(s)
    let curlString = ''
    curlString += `curl  --request ${s.method.toUpperCase()}`
    let url = `${baseUrl}${apiPath}`
    const pathParams = request.path_params || request.pathParams || []
    pathParams.forEach((param) => {
      if (param?.key && param?.example !== undefined) {
        url = url.replace(`:${param.key}`, param.example)
        url = url.replace(`{${param.key}}`, param.example)
      }
    })

    const queryParams = request.query_params || request.query || []
    if(queryParams.length > 0) {
      let query = ''
      queryParams.forEach((param) => {
        if (!param?.key) return
        query += `${query === '' ? '?' : '&'}${param.key}`
        if (param.example !== undefined) {
          query += `=${param.example}`
        }
      })
      if(query) {
        url += query
      }
    }
    curlString += `  \\\n  --url ${url}`
    ;(request.headers || []).forEach((header) => {
      if (header?.key && header?.example !== undefined) {
        curlString += `  \\\n  --header '${header.key}: ${header.example}'`
      }
    })

    const requestBodySchema = request.body?.content?.['application/json']?.schema
    const requestBodyExample = (() => {
      if (Array.isArray(request.body) && request.body.length > 0) {
        const body = {}
        request.body.forEach((item) => {
          if (item?.key) {
            body[item.key] = item.example
          }
        })
        return body
      }
      if (requestBodySchema) {
        return buildExampleFromSchema(requestBodySchema, s.components)
      }
      return undefined
    })()

    if(requestBodyExample !== undefined) {
      curlString += `  \\\n  --header 'Content-Type: application/json'`
      curlString += `  \\\n  --data '${JSON.stringify(requestBodyExample, null, 4)}'`
    }
    return curlString
  }
  

  const renderWebhook = (s, si) => {
    const headers = (s.request?.headers || []).map((h) => typeof h === 'string' ? { key: h, required: false, type: 'string', description: '' } : h)
    const payloadProperties = s.payload ? collectSchemaProperties(s.payload, s.components) : []
    const payloadExample = s.payload ? buildExampleFromSchema(s.payload, s.components) : undefined
    const collapseKey = `webhook_${si}_payload`
    const anchorId = getAnchorId(s)

    return (
      <div id={anchorId || undefined} key={`content_section_${si}`} className="endpoint__wrapper">
        <div className="endpoint__title-wrapper">
          <span className={`method ${s.method}`}>{ (s.method || 'post').toUpperCase() }</span>
          <span className="endpoint__title">{ s.title }</span>
        </div>
        { s.description && <div className="endpoint__description" dangerouslySetInnerHTML={{ __html: s.description }} /> }
        <div className="endpoint__request-wrapper">
          {/* <span className="endpoint__request-title">Webhook</span> */}
          { (headers && headers.length > 0) && (
            <div className="endpoint__request-section">
              <span className="endpoint__request-section-title">Headers</span>
              <div className="endpoint__request-section-wrapper">
              { headers.map((h, hi) => {
                return (
                  <div key={`webhook_header_${hi}`} className="endpoint__request-item" style={getRequestRowStyle(hi === headers.length - 1)}>
                    <div className="endpoint__request-item-key" style={requestItemColumnStyles[0]}>{h.key}</div>
                    <div className={`endpoint__request-item-level ${h.required ? 'required' : 'optional'}`} style={requestItemColumnStyles[1]}>{h.required ? 'required' : 'optional'}</div>
                    <div className="endpoint__request-item-type" style={requestItemColumnStyles[2]}>{h.type}</div>
                    <div className="endpoint__request-item-description" style={requestItemColumnStyles[3]} dangerouslySetInnerHTML={{ __html: h.description || '' }} />
                  </div>
                )
              })}
              </div>
            </div>
          ) }
          { (payloadProperties && payloadProperties.length > 0) && (
            <div className="endpoint__request-section">
              <span className="endpoint__request-section-title">Payload</span>
              { renderPropertyList(payloadProperties, `webhook_payload_${si}`) }
            </div>
          ) }
          { payloadExample !== undefined && (
            <div className="endpoint__request-section">
              <div className="endpoint__request-section-title">Payload example</div>
              <div className="endpoint__request-curl-wrapper">
                <IconButton onClick={() => toggleSection(collapseKey)} className="endpoint__request-example-button" size="small">
                  { Boolean(sectionsCollapsed[collapseKey]) ? <ExpandMore /> : <ExpandLess /> }
                </IconButton>
                <Collapse className={"endpoint__request-example-collapse"} in={!Boolean(sectionsCollapsed[collapseKey])}>
                  <JSONPretty data={payloadExample} theme={jsonTheme} space={2} />
                </Collapse>
              </div>
            </div>
          ) }
        </div>
      </div>
    )
  }

  const renderEndpoint = (s, si) => {
    const request = s.request || {}
    const headers = request.headers || []
    const pathParams = request.path_params || request.pathParams || []
    const queryParams = request.query_params || request.query || []
    const bodySchema = request.body?.content?.['application/json']?.schema
    const bodyProperties = bodySchema ? collectSchemaProperties(bodySchema, s.components) : []
    const responses = s.responses || (s.response ? { default: s.response } : null)
    const anchorId = getAnchorId(s)
    const apiPath = getApiPath(s)

    const responseEntries = responses ? Object.entries(responses) : []

    responseEntries.sort((a, b) => a[0].localeCompare(b[0]))

    return (
      <div id={anchorId || undefined} key={`content_section_${si}`} className="endpoint__wrapper">
        <div className="endpoint__title-wrapper">
          <span className={`method ${s.method}`}>{ s.method.toUpperCase() }</span>
          <span className="endpoint__title">{ s.title }</span>
        </div>
        { s.note && <div className="endpoint__note" dangerouslySetInnerHTML={{ __html: s.note }} /> }
        <code>{ baseUrl }<b>{ apiPath }</b></code>
        <div className="endpoint__description" dangerouslySetInnerHTML={{ __html: s.description }} />
        { s.request && <div className="endpoint__request-wrapper">
            <span className="endpoint__request-title">Request</span>
            { (headers && headers.length > 0) && (
              <div className="endpoint__request-section">
                <span className="endpoint__request-section-title">Headers</span>
                <div className="endpoint__request-section-wrapper">
                { headers.map((h, hi) => {
                  return (
                    <div key={`header_${hi}`} className="endpoint__request-item" style={getRequestRowStyle(hi === headers.length - 1)}>
                      <div className="endpoint__request-item-key" style={requestItemColumnStyles[0]}>{h.key}</div>
                      <div className={`endpoint__request-item-level ${h.required ? 'required' : 'optional'}`} style={requestItemColumnStyles[1]}>{h.required ? 'required' : 'optional'}</div>
                      <div className="endpoint__request-item-type" style={requestItemColumnStyles[2]}>{h.type}</div>
                      <div className="endpoint__request-item-description" style={requestItemColumnStyles[3]} dangerouslySetInnerHTML={{ __html: h.description }} />
                    </div>
                  )
                })}
                </div>
              </div>
            ) }
            { (pathParams && pathParams.length > 0) && (
              <div className="endpoint__request-section">
                <span className="endpoint__request-section-title">Path parameters</span>
                <div className="endpoint__request-section-wrapper">
                { pathParams.map((h, hi) => {
                  return (
                    <div key={`path_param_${hi}`} className="endpoint__request-item" style={getRequestRowStyle(hi === pathParams.length - 1)}>
                      <div className="endpoint__request-item-key" style={requestItemColumnStyles[0]}>{h.key}</div>
                      <div className={`endpoint__request-item-level ${h.required ? 'required' : 'optional'}`} style={requestItemColumnStyles[1]}>{h.required ? 'required' : 'optional'}</div>
                      <div className="endpoint__request-item-type" style={requestItemColumnStyles[2]}>{h.type}</div>
                      <div className="endpoint__request-item-description" style={requestItemColumnStyles[3]} dangerouslySetInnerHTML={{ __html: h.description }} />
                    </div>
                  )
                })}
                </div>
              </div>
            ) }
            { (queryParams && queryParams.length > 0) && (
              <div className="endpoint__request-section">
                <span className="endpoint__request-section-title">Query parameters</span>
                <div className="endpoint__request-section-wrapper">
                { queryParams.map((h, hi) => {
                  return (
                    <div key={`query_param_${hi}`} className="endpoint__request-item" style={getRequestRowStyle(hi === queryParams.length - 1)}>
                      <div className="endpoint__request-item-key" style={requestItemColumnStyles[0]}>{h.key}</div>
                      <div className={`endpoint__request-item-level ${h.required ? 'required' : 'optional'}`} style={requestItemColumnStyles[1]}>{h.required ? 'required' : 'optional'}</div>
                      <div className="endpoint__request-item-type" style={requestItemColumnStyles[2]}>{h.type}</div>
                      <div className="endpoint__request-item-description" style={requestItemColumnStyles[3]} dangerouslySetInnerHTML={{ __html: h.description }} />
                    </div>
                  )
                })}
                </div>
              </div>
            ) }
            { (request.body && Array.isArray(request.body) && request.body.length > 0) && (
              <div className="endpoint__request-section">
                <span className="endpoint__request-section-title">Body</span>
                <div className="endpoint__request-section-wrapper">
                { request.body.map((b, hi) => {
                  return (
                    <div key={`body_param_${hi}`} className="endpoint__request-item" style={getRequestRowStyle(hi === request.body.length - 1)}>
                      <div className="endpoint__request-item-key" style={requestItemColumnStyles[0]}>{b.key}</div>
                      <div className={`endpoint__request-item-level ${b.required ? 'required' : 'optional'}`} style={requestItemColumnStyles[1]}>{b.required ? 'required' : 'optional'}</div>
                      <div className="endpoint__request-item-type" style={requestItemColumnStyles[2]}>{b.type}</div>
                      <div className="endpoint__request-item-description" style={requestItemColumnStyles[3]} dangerouslySetInnerHTML={{ __html: b.description }} />
                    </div>
                  )
                })}
                </div>
              </div>
            ) }
            { (bodyProperties && bodyProperties.length > 0) && (
              <div className="endpoint__request-section">
                <span className="endpoint__request-section-title">Body</span>
                { renderPropertyList(bodyProperties, `body_${si}`) }
              </div>
            ) }
            <div className="endpoint__request-section-title">Example:</div>
            <div className="endpoint__request-curl-wrapper">
              { curlExample(s) }
            </div>
          </div>
        }
        { responseEntries.length > 0 && (
          <div className="endpoint__request-wrapper">
            <div className="endpoint__request-title">Response</div>
            { responseEntries.map(([statusCode, response]) => {
              const responseContent = response?.content?.['application/json']
              const schema = responseContent?.schema || response?.schema
              const responseProperties = schema ? collectSchemaProperties(schema, s.components) : []
              const responseExample = responseContent?.example !== undefined
                ? responseContent.example
                : schema
                  ? buildExampleFromSchema(schema, s.components)
                  : response?.example
              const collapseKey = `${requestId(s)}_${statusCode}`

              return (
                <div key={`response_${statusCode}`} className="endpoint__request-section">
                  <div className="endpoint__request-section-title">Status {statusCode}</div>
                  { response?.description && (
                    <div className="endpoint__description" dangerouslySetInnerHTML={{ __html: response.description }} />
                  ) }
                  { responseProperties.length > 0 && renderPropertyList(responseProperties, `response_${statusCode}`) }
                  { responseExample !== undefined && (
                    <>
                      <div className="endpoint__request-section-title">Example:</div>
                      <div className="endpoint__request-curl-wrapper">
                        <IconButton onClick={() => toggleSection(collapseKey)} className="endpoint__request-example-button" size="small">
                          { Boolean(sectionsCollapsed[collapseKey]) ? <ExpandMore /> : <ExpandLess /> }
                        </IconButton>
                        <Collapse className={"endpoint__request-example-collapse"} in={!Boolean(sectionsCollapsed[collapseKey])}>
                          <JSONPretty data={responseExample} theme={jsonTheme} space={2} />
                        </Collapse>
                      </div>
                    </>
                  ) }
                </div>
              )
            }) }
          </div>
        ) }
      </div>
    )
  }

  return (
    <div>
      { pageContent.map(renderSection) }
    </div>
  )

}

export default Content
